import {
  RtkErrorResponseOfVsApi,
  RtkErrorResponseOfVsGraphQL,
  VsErrorData,
} from './type';

type Validator = (value: any) => boolean;

type ErrorTarget = Record<string, unknown>;

const is = {
  string: (value: unknown): value is string => typeof value === 'string',
  number: (value: unknown): value is number => typeof value === 'number',
  object: (value: unknown): value is object =>
    value !== null && typeof value === 'object',
  arrayOf: (validator: Validator) => (value: unknown) =>
    Array.isArray(value) && value.every(validator),
};

const logical = {
  or: (validators: Validator[]) => (value: unknown) =>
    validators.some(vl => vl(value)),
  and: (validators: Validator[]) => (value: unknown) =>
    validators.every(vl => vl(value)),
};

const generateRulesValidator = <T extends Record<string, unknown>>(rules: {
  [key in keyof T]: Validator;
}) => {
  return (target: ErrorTarget) =>
    Object.entries<Validator>(rules).every(
      ([key, validator]) => key in target && validator(target[key])
    );
};

const vsApiDataValidator = generateRulesValidator<VsErrorData>({
  code: is.string,
  message: logical.or([is.string, is.arrayOf(is.string), is.object]),
  path: is.string,
  request_id: is.string,
  service: is.string,
  status_code: is.number,
  timestamp: is.string,
});
export function isErrorDataOfVsApi(
  errorData: unknown
): errorData is VsErrorData {
  return is.object(errorData) && vsApiDataValidator(errorData as ErrorTarget);
}

const vsApiResponseValidator = generateRulesValidator({
  data: isErrorDataOfVsApi,
  status: is.number,
});
export function isErrorOfVsApi(
  error: unknown
): error is RtkErrorResponseOfVsApi {
  return is.object(error) && vsApiResponseValidator(error as ErrorTarget);
}

const isErrorItemsOfVsGraphQL = generateRulesValidator({
  message: is.string,
});
const vsGraphQLValidator = generateRulesValidator({
  status: is.number,
  errors: is.arrayOf(isErrorItemsOfVsGraphQL),
});
export function isErrorOfVsGraphQL(
  error: unknown
): error is RtkErrorResponseOfVsGraphQL {
  return is.object(error) && vsGraphQLValidator(error as ErrorTarget);
}

type ResClientError = {
  status: 400;
  data: {
    request_id: string;
    code: string;
    status_code: number;
    service: string;
    message: {
      [key in string]: string | number | object;
    };
    path: string;
    timestamp: string;
  };
};

export function isErrorOfClient(error: unknown): error is ResClientError {
  return is.object(error) && 'status' in error && error.status === 400;
}

export function isEmailLinkExpiredError(err: unknown) {
  return (
    isErrorOfVsApi(err) &&
    err.data.status_code === 400 &&
    typeof err.data.message === 'string' &&
    err.data.message
      .toLowerCase()
      .includes('contact email verify request has expired')
  );
}

export function isUnAuthError(e: unknown) {
  if (isErrorOfVsGraphQL(e)) {
    const isErr401 = e.errors.find(e => e.code === 'UNAUTHENTICATED');
    if (isErr401) {
      return true;
    }
  }
  if (isErrorOfVsApi(e)) {
    const isErr401 =
      e.status === 401 && e.data.code === 'UnauthorizedException';
    if (isErr401) {
      return true;
    }
  }
  return false;
}
